import { forwardRef, type ElementType } from 'react'
import MuiIconButton, { type IconButtonProps } from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

type Props = IconButtonProps & { component?: ElementType; href?: string; to?: string }

// MUI's IconButton plus a hint: the tooltip text is `title`, else `aria-label`,
// so every icon-only button explains itself without a call-site change.
// Hover and keyboard focus use MUI's defaults. A touch screen has no hover, so
// a press-and-hold (400 ms, well under the 500 ms the browser takes for its
// own context menu) shows the label for three seconds; a plain tap still just
// acts. The callout and text selection are switched off so the hold does not
// compete with the OS.
export const IconButton = forwardRef<HTMLButtonElement, Props>(function IconButton(
  { title, sx, disabled, ...rest },
  ref,
) {
  const label = title ?? (typeof rest['aria-label'] === 'string' ? rest['aria-label'] : undefined)
  const button = (
    <MuiIconButton
      ref={ref}
      disabled={disabled}
      sx={[{ WebkitTouchCallout: 'none', userSelect: 'none' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    />
  )
  if (!label) return button
  return (
    <Tooltip title={label} arrow enterTouchDelay={400} leaveTouchDelay={3000}>
      {/* A disabled button swallows the pointer events the tooltip listens for. */}
      {disabled ? <span style={{ display: 'inline-flex' }}>{button}</span> : button}
    </Tooltip>
  )
}) as unknown as typeof MuiIconButton
